type Fmt =
  | 'yyyy-MM-dd'
  | 'yyyy-MM-dd hh:mm:ss'
  | 'yyyy年MM月dd日 HH:mm:ss'
  | 'yyyy年MM月dd日'
  | 'yyyyMMdd'
  | ' MM/dd/yyyy'
/**
 *
 * @param date
 * @param fmt  yyyy-MM-dd、yyyy-MM-dd hh:mm:ss、yyyy年MM月dd日 HH:mm:ss
 * @returns
 */
export const dateFormat = (date: string | number | Date, fmt: Fmt = 'yyyy-MM-dd') => {
  // 智能日期解析函数
  const parseDate = (input: string | number | Date): Date => {
    if (input instanceof Date) return input
    if (typeof input === 'number') return new Date(input)

    // 处理字符串类型
    let dateStr = input.trim()

    // 处理IOS日期格式兼容性问题
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      dateStr = dateStr.replace(/-/g, '/')
    }

    // 尝试直接解析
    const parsedDate = new Date(dateStr)
    if (!isNaN(parsedDate.getTime())) return parsedDate

    // 尝试处理各种自定义格式 - 修复不必要的转义字符
    const formats = [
      /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/, // yyyy-MM-dd (修复了不必要的转义)
      /^(\d{4})[-/](\d{1,2})[-/](\d{1,2}) (\d{1,2}):(\d{1,2}):(\d{1,2})$/, // yyyy-MM-dd HH:mm:ss
      /^(\d{4})[-/](\d{1,2})[-/](\d{1,2}) (\d{1,2}):(\d{1,2})$/, // yyyy-MM-dd HH:mm
      /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T ](\d{1,2}):(\d{1,2}):(\d{1,2})\.(\d{3})Z?$/, // ISO with milliseconds
      /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T ](\d{1,2}):(\d{1,2}):(\d{1,2})Z?$/, // ISO without milliseconds
      /^(\d{4})(\d{2})(\d{2})$/, // yyyyMMdd
      /^(\d{4})(\d{2})(\d{2})T?(\d{2})(\d{2})(\d{2})Z?$/, // compact ISO
      /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/ // MM/dd/yyyy (修复了不必要的转义)
    ]

    for (const format of formats) {
      const match = dateStr.match(format)
      if (match) {
        // 提取日期组件
        let year = 0,
          month = 0,
          day = 0,
          hours = 0,
          minutes = 0,
          seconds = 0,
          milliseconds = 0

        // 根据匹配组确定日期组件
        if (
          format === formats[0] ||
          format === formats[1] ||
          format === formats[2] ||
          format === formats[3] ||
          format === formats[4]
        ) {
          year = parseInt(match[1], 10)
          month = parseInt(match[2], 10) - 1
          day = parseInt(match[3], 10)

          if (match[4]) hours = parseInt(match[4], 10)
          if (match[5]) minutes = parseInt(match[5], 10)
          if (match[6]) seconds = parseInt(match[6], 10)
          if (match[7]) milliseconds = parseInt(match[7], 10)
        } else if (format === formats[5] || format === formats[6]) {
          year = parseInt(match[1], 10)
          month = parseInt(match[2], 10) - 1
          day = parseInt(match[3], 10)

          if (match[4]) hours = parseInt(match[4], 10)
          if (match[5]) minutes = parseInt(match[5], 10)
          if (match[6]) seconds = parseInt(match[6], 10)
        } else if (format === formats[7]) {
          year = parseInt(match[3], 10)
          month = parseInt(match[1], 10) - 1
          day = parseInt(match[2], 10)
        }

        // 创建日期对象
        const dateObj: Date = new Date(year, month, day, hours, minutes, seconds, milliseconds)

        // 验证日期有效性
        if (
          !isNaN(dateObj.getTime()) &&
          dateObj.getFullYear() === year &&
          dateObj.getMonth() === month &&
          dateObj.getDate() === day
        ) {
          return dateObj
        }
      }
    }

    // 如果所有解析都失败，抛出错误
    throw new Error(`无法解析的日期格式: ${input}`)
  }

  // 解析日期
  let dateObj: Date
  try {
    dateObj = parseDate(date)
  } catch (error) {
    console.error(error)
    return 'Invalid Date'
  }

  // 提取日期各部分
  const year = dateObj.getFullYear()
  const month = dateObj.getMonth() + 1 // 月份从0开始
  const day = dateObj.getDate()
  const hours = dateObj.getHours()
  const minutes = dateObj.getMinutes()
  const seconds = dateObj.getSeconds()
  const milliseconds = dateObj.getMilliseconds()

  // 定义替换规则
  const replacements: Record<string, string> = {
    yyyy: year.toString(),
    yy: year.toString().slice(-2).padStart(2, '0'),
    MM: month.toString().padStart(2, '0'),
    M: month.toString(),
    dd: day.toString().padStart(2, '0'),
    d: day.toString(),
    HH: hours.toString().padStart(2, '0'),
    H: hours.toString(),
    hh: (hours % 12 || 12).toString().padStart(2, '0'),
    h: (hours % 12 || 12).toString(),
    mm: minutes.toString().padStart(2, '0'),
    m: minutes.toString(),
    ss: seconds.toString().padStart(2, '0'),
    s: seconds.toString(),
    SSS: milliseconds.toString().padStart(3, '0'),
    SS: milliseconds.toString().padStart(3, '0').slice(0, 2),
    S: milliseconds.toString().padStart(3, '0').slice(0, 1),
    a: hours < 12 ? 'am' : 'pm',
    A: hours < 12 ? 'AM' : 'PM',
    // 中文格式支持
    年: '年',
    月: '月',
    日: '日',
    时: '时',
    分: '分',
    秒: '秒',
    MM月: `${month.toString().padStart(2, '0')}月`,
    M月: `${month}月`,
    dd日: `${day.toString().padStart(2, '0')}日`,
    d日: `${day}日`
  }

  // 使用正则替换所有匹配项（按长度降序确保优先匹配长格式）
  const patterns = Object.keys(replacements).sort((a, b) => b.length - a.length)
  let result = fmt

  for (const pattern of patterns) {
    const regex = new RegExp(pattern, 'g')
    result = result.replace(regex, replacements[pattern]) as Fmt
  }

  return result
}
